import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  GraphicEq as ListenIcon,
  Description as NoteIcon,
  Translate as PlainIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import AuthShell from '../components/AuthShell';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function formatLoginError(err) {
  const status = err.response?.status;
  const detail = err.response?.data?.detail;
  const detailStr =
    typeof detail === 'string'
      ? detail
      : Array.isArray(detail)
        ? detail.map((d) => (typeof d === 'object' && d?.msg ? d.msg : String(d))).join(' ')
        : '';

  if (status === 401 || status === 403) {
    const lower = detailStr.toLowerCase();
    if (
      !detailStr ||
      lower.includes('invalid') ||
      lower.includes('credential') ||
      lower.includes('incorrect')
    ) {
      return 'Incorrect email or password. Check that both are correct and try again.';
    }
    return detailStr || 'Sign-in was denied. Try again or contact your clinic administrator.';
  }
  if (detailStr) {
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((d) => (typeof d === 'object' && d?.msg ? d.msg : String(d)))
        .join(' ');
    }
  }
  if (err.message && err.message !== 'Network Error') return err.message;
  if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
    return `Cannot reach ${API_URL}. Start the backend on port 8000, or set REACT_APP_API_URL. Using a LAN IP? Add that URL to CORS_ORIGINS in server/backend/.env.`;
  }
  return 'Login failed';
}

const TEAL = '#5eead4';

// A still of the product doing its job: a consult being heard, a note forming.
// Purely illustrative — no real patient, no numbers that could read as a claim.
function ConsultPreview() {
  const bar = (w, o = 0.22) => (
    <Box sx={{ height: 7, width: w, borderRadius: 99, bgcolor: `rgba(255,255,255,${o})` }} />
  );
  return (
    <Box
      sx={{
        borderRadius: '16px',
        border: '1px solid rgba(255,255,255,0.14)',
        bgcolor: 'rgba(4, 47, 46, 0.55)',
        backdropFilter: 'blur(8px)',
        boxShadow: '0 30px 60px -20px rgba(0,0,0,0.45)',
        overflow: 'hidden',
        maxWidth: 520,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 8, height: 8, borderRadius: '50%', bgcolor: TEAL,
              animation: 'livePulse 1.8s ease-in-out infinite',
              '@keyframes livePulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
            }}
          />
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
            Exam room 3 · Listening
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 16 }} aria-hidden>
          {[6, 12, 9, 15, 7, 11, 5].map((h, i) => (
            <Box
              key={i}
              sx={{
                width: 3, height: h, borderRadius: 2, bgcolor: TEAL, opacity: 0.8,
                animation: `eq 1.${i + 2}s ease-in-out infinite`,
                '@keyframes eq': { '0%, 100%': { transform: 'scaleY(1)' }, '50%': { transform: 'scaleY(0.4)' } },
                transformOrigin: 'bottom',
              }}
            />
          ))}
        </Box>
      </Box>
      <Box sx={{ p: 2, display: 'grid', gap: 1.5 }}>
        <Typography sx={{ fontSize: 13, lineHeight: 1.55, color: 'rgba(255,255,255,0.82)' }}>
          <Box component="span" sx={{ color: TEAL, fontWeight: 700 }}>Patient · </Box>
          “Just some fluttering in my chest after my second espresso — no dizziness.”
        </Typography>
        <Box sx={{ p: 1.75, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(94, 234, 212, 0.25)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.25 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.8, color: TEAL }}>SOAP NOTE · DRAFTING</Typography>
            <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>ICD-10 suggested</Typography>
          </Box>
          {['S', 'O', 'A', 'P'].map((k, i) => (
            <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: i ? 0.9 : 0 }}>
              <Typography sx={{ width: 14, fontSize: 12, fontWeight: 800, color: 'rgba(255,255,255,0.75)' }}>{k}</Typography>
              {bar(['78%', '56%', '66%', '42%'][i], i === 3 ? 0.12 : 0.22)}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#fff',
    '&.Mui-focused fieldset': { borderWidth: '2px' },
  },
};

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { unifiedLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get('provider_activated') === '1') {
      toast.success('Registration confirmed. You can sign in.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { routed } = await unifiedLogin(email, password);
      if (routed) {
        // Home portal is elsewhere — the browser is redirecting there.
        toast.success('Taking you to your portal…');
        return;
      }
      // Return to the page the emailed link pointed at, if any.
      const from = location.state?.from;
      navigate(from && !from.startsWith('/login') ? from : '/', { replace: true });
    } catch (err) {
      const message = formatLoginError(err);
      setError(message);
      toast.error(message, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      portalName="Provider portal"
      panelBackground="linear-gradient(155deg, #042f2e 0%, #0b4f4a 55%, #0f766e 100%)"
      accent={TEAL}
      headlineFont='"Source Serif 4", Georgia, serif'
      eyebrow="Ambient AI medical scribe"
      headline={<>Spend time with patients.<br /><Box component="span" sx={{ color: TEAL }}>Not paper charts.</Box></>}
      body="ScribeLive listens to the consult and hands you a structured note to review and sign — for solo practices, clinics and multi-site groups."
      points={[
        { icon: <ListenIcon />, title: 'Notes while you talk', text: 'SOAP notes drafted from the conversation, ready when the visit ends.' },
        { icon: <NoteIcon />, title: 'Coding, suggested', text: 'ICD-10 codes proposed alongside the assessment for you to confirm.' },
        { icon: <PlainIcon />, title: 'A summary your patient can read', text: 'Plain-language visit notes sent to the patient portal.' },
      ]}
      preview={<ConsultPreview />}
    >
      <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 1.2 }}>
        Provider portal
      </Typography>
      <Typography variant="h4" component="h2" sx={{ mt: 0.5, mb: 1 }}>
        Welcome back
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Sign in to see today’s schedule, queue and notes.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          autoComplete="email"
          InputLabelProps={{ shrink: true }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          required
          sx={fieldSx}
        />
        <TextField
          fullWidth
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          InputLabelProps={{ shrink: true }}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((v) => !v)}
                  edge="end"
                  // Announced rather than left as a bare icon, and never a tab
                  // stop between the password and the submit button.
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <HideIcon /> : <ShowIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={fieldSx}
        />
        <Button
          fullWidth
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
          sx={{
            mt: 3,
            py: 1.5,
            background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
            fontWeight: 700,
            fontSize: '1rem',
            boxShadow: '0 4px 14px 0 rgba(13, 148, 136, 0.39)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
              boxShadow: '0 6px 20px 0 rgba(13, 148, 136, 0.45)',
            },
          }}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>
      </form>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        Trouble signing in? Your clinic administrator can reset your access.
      </Typography>
    </AuthShell>
  );
}
